import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import productsService from "../services/productsService";
import { STORAGE_KEYS } from "../hooks/useNavigationMemory";
import useNavigationMemory from "../hooks/useNavigationMemory";

// 🆕 FUNCIÓN: Parsear query parameters de la URL
const parseQueryParams = (search) => {
  const params = new URLSearchParams(search);
  const filters = {};

  // Mapear parámetros de URL a filtros
  if (params.get("category")) filters.category = params.get("category");
  if (params.get("brand")) filters.brand = params.get("brand");
  if (params.get("model")) filters.model = params.get("model");
  if (params.get("estado")) filters.estado = params.get("estado"); // ✅ CORREGIDO: usar estado
  if (params.get("ubicacion")) filters.ubicacion = params.get("ubicacion");
  if (params.get("search")) filters.search = params.get("search");

  // Rangos de precio
  if (params.get("minPrice") && params.get("maxPrice")) {
    filters.priceRange = [
      parseInt(params.get("minPrice")),
      parseInt(params.get("maxPrice")),
    ];
  }

  // Rangos de año
  if (params.get("minYear") && params.get("maxYear")) {
    filters.yearRange = [
      parseInt(params.get("minYear")),
      parseInt(params.get("maxYear")),
    ];
  }

  // Features como array
  if (params.get("features")) {
    try {
      filters.features = JSON.parse(decodeURIComponent(params.get("features")));
    } catch {
      filters.features = params.get("features").split(",");
    }
  }

  console.log("📋 Parsed query params:", filters);
  return filters;
};

// 🆕 FUNCIÓN: Convertir filtros a query parameters
const filtersToQueryParams = (filters) => {
  const params = new URLSearchParams();

  if (filters.search && filters.search.trim()) {
    params.set("search", filters.search.trim());
  }
  if (filters.category && filters.category !== "Todas") {
    params.set("category", filters.category);
  }
  if (filters.brand && filters.brand !== "Todas") {
    params.set("brand", filters.brand);
  }
  if (filters.model && filters.model !== "Todas") {
    params.set("model", filters.model);
  }
  if (filters.estado && filters.estado !== "Todas") {
    // ✅ CORREGIDO: usar estado
    params.set("estado", filters.estado);
  }
  if (filters.ubicacion && filters.ubicacion !== "Todas") {
    params.set("ubicacion", filters.ubicacion);
  }

  // Solo agregar rangos si no son los valores por defecto
  if (filters.priceRange && Array.isArray(filters.priceRange)) {
    if (filters.priceRange[0] > 0 || filters.priceRange[1] < 500000000) {
      params.set("minPrice", filters.priceRange[0].toString());
      params.set("maxPrice", filters.priceRange[1].toString());
    }
  }

  if (filters.yearRange && Array.isArray(filters.yearRange)) {
    if (filters.yearRange[0] > 2015 || filters.yearRange[1] < 2025) {
      params.set("minYear", filters.yearRange[0].toString());
      params.set("maxYear", filters.yearRange[1].toString());
    }
  }

  if (filters.features && filters.features.length > 0) {
    params.set(
      "features",
      encodeURIComponent(JSON.stringify(filters.features))
    );
  }

  return params.toString();
};

// Inicializar filtros desde URL params o sessionStorage
const getInitialFilters = (initialFilters, location) => {
  // 🔥 PRIORIDAD 1: Query parameters de la URL
  const urlFilters = parseQueryParams(location?.search || "");
  if (Object.keys(urlFilters).length > 0) {
    console.log("🌐 Initializing filters from URL params:", urlFilters);
    const initializedFilters = {
      search: "",
      category: "Todas",
      brand: "Todas",
      model: "Todas",
      priceRange: [0, 500000000],
      yearRange: [2015, 2025],
      estado: "Todas",
      ubicacion: "Todas",
      features: [],
      ...initialFilters,
      ...urlFilters, // URL params tienen prioridad máxima
    };

    // 🔥 MARCAR QUE ESTOS FILTROS VIENEN DE URL PARAMS PARA AUTO-APLICAR
    initializedFilters._fromUrlParams = true;

    return initializedFilters;
  }

  // 🔥 PRIORIDAD 2: sessionStorage (para navegación de regreso)
  const saved = sessionStorage.getItem(STORAGE_KEYS.FILTERS_STATE);
  if (saved) {
    try {
      const savedFilters = JSON.parse(saved);
      console.log("💾 Initializing filters from sessionStorage:", savedFilters);
      return {
        ...savedFilters,
        ...initialFilters, // initialFilters pueden sobreescribir algunos valores
      };
    } catch {
      // Si hay error parseando, usar valores por defecto
    }
  }

  // 🔥 PRIORIDAD 3: Valores por defecto
  console.log("🔄 Initializing default filters");
  return {
    search: "",
    category: "Todas",
    brand: "Todas",
    model: "Todas",
    priceRange: [0, 500000000],
    yearRange: [2015, 2025],
    estado: "Todas", // ✅ CORREGIDO: usar estado con valores: "operativo", "no operativo", null
    ubicacion: "Todas",
    features: [],
    ...initialFilters,
  };
};

const useServerFilters = (initialFilters = {}, products = []) => {
  const location = useLocation();
  const navigate = useNavigate();

  // 🆕 USAR TRACKING DE NAVEGACIÓN - MOVER AL NIVEL SUPERIOR
  const {
    isReturningFromProduct,
    isNewNavigationWithFilters,
    clearNavigationTracking,
  } = useNavigationMemory();

  // Estado de filtros REALES - inicializados con URL params
  const [filters, setFilters] = useState(() =>
    getInitialFilters(initialFilters, location)
  );

  // 🎯 NUEVOS ESTADOS PARA FILTROS TEMPORALES
  const [tempFilters, setTempFilters] = useState(filters);
  const [hasUnappliedChanges, setHasUnappliedChanges] = useState(false);

  // Estado para opciones de filtros (categorías, marcas, modelos, etc.)
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    brands: [], // ✅ NUEVO: Array de marcas
    models: [], // ✅ ACTUALIZADO: Array de modelos
    locations: [],
    features: [],
    priceRange: { min: 0, max: 500000000 },
    yearRange: { min: 2015, max: 2025 },
  });

  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState(null);

  // 🔄 Sincronizar filtros temporales cuando cambien los filtros reales
  useEffect(() => {
    setTempFilters(filters);
    setHasUnappliedChanges(false);
  }, [filters]);

  // Guardar filtros en sessionStorage cada vez que cambian
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEYS.FILTERS_STATE, JSON.stringify(filters));
  }, [filters]);

  // 🎯 NUEVA FUNCIÓN: Actualizar filtros temporales
  const updateTempFilters = useCallback((newTempFilters) => {
    console.log("🔄 Updating temp filters:", newTempFilters);
    setTempFilters((prev) => ({ ...prev, ...newTempFilters }));
    setHasUnappliedChanges(true);
  }, []);

  // 🎯 NUEVA FUNCIÓN: Aplicar filtros temporales a filtros reales
  const applyFilters = useCallback(() => {
    console.log("🎯 Applying temp filters to real filters:", tempFilters);

    // Aplicar los filtros temporales a los filtros reales
    setFilters(tempFilters);
    setHasUnappliedChanges(false);

    // 🔥 NUEVA ESTRATEGIA: Disparar evento personalizado para forzar reset de productos
    // Similar a como SearchFilter actualiza inmediatamente
    window.dispatchEvent(
      new CustomEvent("filtersApplied", {
        detail: { filters: tempFilters, timestamp: Date.now() },
      })
    );

    // Sincronizar filtros con la URL
    const queryParams = filtersToQueryParams(tempFilters);
    navigate(`?${queryParams}`, { replace: true });
  }, [tempFilters, navigate]);

  // 🔧 ESTABILIZAR REFERENCIA: Usar useMemo para evitar re-creación innecesaria del objeto filters
  const priceRange0 = filters.priceRange?.[0];
  const priceRange1 = filters.priceRange?.[1];
  const yearRange0 = filters.yearRange?.[0];
  const yearRange1 = filters.yearRange?.[1];
  const featuresString = filters.features?.join(",") || "";

  const stableFilters = useMemo(
    () => filters,
    [
      filters.search,
      filters.category,
      filters.brand,
      filters.model,
      priceRange0,
      priceRange1,
      yearRange0,
      yearRange1,
      filters.estado,
      filters.ubicacion,
      featuresString,
    ]
  );

  // 🎯 NUEVA FUNCIÓN: Resetear filtros temporales
  const resetTempFilters = useCallback(() => {
    console.log("🔄 Resetting temp filters to real filters");
    setTempFilters(filters);
    setHasUnappliedChanges(false);
  }, [filters]);

  // Cargar opciones de filtros (metadatos) al inicializar
  const loadFilterOptions = useCallback(async () => {
    setOptionsLoading(true);
    setOptionsError(null);

    try {
      console.log("🔧 Loading filter options from API...");

      // Llamada ligera para obtener solo metadatos de filtros
      const response = await productsService.getFilterOptions();

      if (response && response.filterOptions) {
        setFilterOptions(response.filterOptions);

        // Actualizar rangos por defecto si no están personalizados
        setFilters((prev) => ({
          ...prev,
          priceRange:
            prev.priceRange[1] === 500000000
              ? [
                  response.filterOptions.priceRange.min,
                  response.filterOptions.priceRange.max,
                ]
              : prev.priceRange,
          yearRange:
            prev.yearRange[1] === 2025
              ? [
                  response.filterOptions.yearRange.min,
                  response.filterOptions.yearRange.max,
                ]
              : prev.yearRange,
        }));

        console.log("✅ Filter options loaded:", {
          categories: response.filterOptions.categories.length,
          models:
            response.filterOptions.models?.length ||
            response.filterOptions.brands?.length ||
            0, // Manejar ambos casos
          locations: response.filterOptions.locations.length,
          features: response.filterOptions.features.length,
          priceRange: response.filterOptions.priceRange,
          yearRange: response.filterOptions.yearRange,
        });
      }
    } catch (error) {
      console.error("❌ Error loading filter options:", error);
      setOptionsError(error.message);
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  // 🆕 FUNCIÓN TEMPORAL: Extraer opciones de filtros desde productos cargados
  const extractFilterOptionsFromProducts = useCallback((products) => {
    if (!products || products.length === 0) return null;

    // Extraer marcas únicas
    const brands = [
      ...new Set(products.map((p) => p.brand).filter(Boolean)),
    ].sort();

    // Extraer modelos únicos
    const models = [
      ...new Set(products.map((p) => p.model).filter(Boolean)),
    ].sort();

    // Extraer categorías únicas
    const categories = [
      ...new Set(products.map((p) => p.category).filter(Boolean)),
    ].sort();

    // Extraer ubicaciones únicas
    const locations = [
      ...new Set(products.map((p) => p.ubicacion).filter(Boolean)),
    ].sort();

    // Extraer características únicas
    const features = [
      ...new Set(products.flatMap((p) => p.features || []).filter(Boolean)),
    ].sort();

    // Calcular rangos de precios
    const prices = products.map((p) => p.price).filter(Boolean);
    const priceRange = {
      min: Math.min(...prices),
      max: Math.max(...prices),
    };

    // Calcular rangos de años
    const years = products.map((p) => p.year).filter(Boolean);
    const yearRange = {
      min: Math.min(...years),
      max: Math.max(...years),
    };

    console.log("📊 Extracted filter options from products:", {
      brands: brands.length,
      models: models.length,
      categories: categories.length,
      locations: locations.length,
      features: features.length,
      priceRange,
      yearRange,
    });

    return {
      brands,
      models,
      categories,
      locations,
      features,
      priceRange,
      yearRange,
    };
  }, []);

  // 🆕 EFECTO: Actualizar opciones de filtros con datos de productos si el backend no los provee
  useEffect(() => {
    // Solo actualizar si tenemos productos pero las opciones están vacías
    if (
      products.length > 0 &&
      (!filterOptions.brands?.length || !filterOptions.models?.length)
    ) {
      const extractedOptions = extractFilterOptionsFromProducts(products);

      if (extractedOptions) {
        console.log("🔄 Updating filter options from loaded products...");
        setFilterOptions((prev) => ({
          ...prev,
          brands: extractedOptions.brands,
          models: extractedOptions.models,
          categories: prev.categories.length
            ? prev.categories
            : extractedOptions.categories,
          locations: prev.locations.length
            ? prev.locations
            : extractedOptions.locations,
          features: prev.features.length
            ? prev.features
            : extractedOptions.features,
          priceRange: extractedOptions.priceRange,
          yearRange: extractedOptions.yearRange,
        }));
      }
    }
  }, [
    products,
    filterOptions.brands,
    filterOptions.models,
    extractFilterOptionsFromProducts,
  ]);

  // Cargar opciones al montar el componente
  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  // 🆕 EFECTO INTELIGENTE: Auto-aplicar filtros cuando vienen de URL params
  useEffect(() => {
    if (filters._fromUrlParams) {
      console.log("🎯 Auto-applying filters from URL params:", filters);

      // Limpiar el flag
      const cleanFilters = { ...filters };
      delete cleanFilters._fromUrlParams;
      setFilters(cleanFilters);

      // 🧠 LÓGICA INTELIGENTE MEJORADA: Análisis más detallado
      const isReturning = isReturningFromProduct();
      const isNewWithFilters = isNewNavigationWithFilters(cleanFilters);

      console.log("🔍 Navigation analysis:", {
        isReturning,
        isNewWithFilters,
        hasUrlFilters: Object.keys(cleanFilters).some((key) => {
          const value = cleanFilters[key];
          if (typeof value === "string")
            return value !== "" && value !== "Todas";
          if (Array.isArray(value)) return value.length > 0;
          return false;
        }),
      });

      let shouldClearCache = false;
      let reason = "";

      if (isReturning) {
        // 🔄 Es navegación de regreso → SIEMPRE preservar caché
        reason = "returning from product - preserving cache";
      } else if (isNewWithFilters) {
        // 🔥 Es navegación nueva con filtros → Limpiar caché
        shouldClearCache = true;
        reason = "new navigation with filters - clearing cache";
      } else {
        // 🤔 Caso ambiguo → Ser conservador y NO limpiar
        reason = "ambiguous case - preserving cache to be safe";
      }

      console.log(`🎯 Cache decision: ${reason}`);

      if (shouldClearCache) {
        console.log("🧹 Clearing cache due to new navigation with filters");
        sessionStorage.removeItem(STORAGE_KEYS.PRODUCTS_STATE);
        sessionStorage.removeItem(STORAGE_KEYS.SCROLL_POSITION);
      } else {
        console.log("💾 Preserving cache");
      }

      // Disparar evento para aplicar filtros automáticamente
      window.dispatchEvent(
        new CustomEvent("filtersApplied", {
          detail: {
            filters: cleanFilters,
            timestamp: Date.now(),
            source: "url-params-auto-apply",
            shouldPreserveCache: !shouldClearCache,
            reason: reason,
          },
        })
      );

      // 🧹 IMPORTANTE: Limpiar tracking después de usar para evitar que se mantenga indefinidamente
      // 🔧 FIX: Usar setTimeout con la función ya extraída del hook
      setTimeout(() => {
        clearNavigationTracking();
      }, 100);
    }
  }, [
    filters,
    isReturningFromProduct,
    isNewNavigationWithFilters,
    clearNavigationTracking,
  ]);

  // 🆕 ESTADO: Trackear la URL anterior para detectar cambios reales
  const [previousSearch, setPreviousSearch] = useState(location.search);

  // 🆕 EFECTO MEJORADO: Solo procesar cuando HAY CAMBIOS REALES en query params
  useEffect(() => {
    const currentSearch = location.search;

    // 🔍 VERIFICACIÓN: Solo procesar si la URL realmente cambió
    if (currentSearch === previousSearch) {
      console.log("🟡 URL didn't change, skipping filter processing");
      return;
    }

    console.log("🌐 URL actually changed:", {
      from: previousSearch,
      to: currentSearch,
    });

    // Actualizar el tracking de URL anterior
    setPreviousSearch(currentSearch);

    const urlFilters = parseQueryParams(currentSearch);

    if (Object.keys(urlFilters).length > 0) {
      console.log("🌐 URL has query params, updating filters:", urlFilters);

      // Construir nuevos filtros con valores por defecto + URL params
      const newFilters = {
        search: "",
        category: "Todas",
        brand: "Todas",
        model: "Todas",
        priceRange: [
          filterOptions.priceRange.min || 0,
          filterOptions.priceRange.max || 500000000,
        ],
        yearRange: [
          filterOptions.yearRange.min || 2015,
          filterOptions.yearRange.max || 2025,
        ],
        estado: "Todas",
        ubicacion: "Todas",
        features: [],
        ...urlFilters, // URL params sobrescriben defaults
      };

      // 🔍 VERIFICACIÓN: Solo aplicar si los filtros realmente cambiaron
      const currentFiltersString = JSON.stringify(filters);
      const newFiltersString = JSON.stringify(newFilters);

      if (currentFiltersString !== newFiltersString) {
        console.log("✅ Filters actually changed, applying new filters");

        setFilters(newFilters);
        setTempFilters(newFilters);
        setHasUnappliedChanges(false);

        // 🔥 AUTO-APLICAR cuando los filtros realmente cambian
        window.dispatchEvent(
          new CustomEvent("filtersApplied", {
            detail: {
              filters: newFilters,
              timestamp: Date.now(),
              source: "url-change-real-change",
            },
          })
        );
      } else {
        console.log("🟡 Filters are the same, no action needed");
      }
    } else {
      // 🧹 URL no tiene query params
      const hasActiveFilters = activeFiltersCount > 0;

      if (hasActiveFilters) {
        console.log("🧹 URL cleared, clearing filters");

        const clearedFilters = {
          search: "",
          category: "Todas",
          brand: "Todas",
          model: "Todas",
          priceRange: [
            filterOptions.priceRange.min || 0,
            filterOptions.priceRange.max || 500000000,
          ],
          yearRange: [
            filterOptions.yearRange.min || 2015,
            filterOptions.yearRange.max || 2025,
          ],
          estado: "Todas",
          ubicacion: "Todas",
          features: [],
        };

        setFilters(clearedFilters);
        setTempFilters(clearedFilters);
        setHasUnappliedChanges(false);

        // Auto-aplicar filtros limpios
        window.dispatchEvent(
          new CustomEvent("filtersApplied", {
            detail: {
              filters: clearedFilters,
              timestamp: Date.now(),
              source: "url-cleared-auto-apply",
            },
          })
        );
      } else {
        console.log("🟡 No active filters and no URL params, no action needed");
      }
    }
  }, [
    location.search,
    filterOptions.priceRange.min,
    filterOptions.priceRange.max,
    filterOptions.yearRange.min,
    filterOptions.yearRange.max,
    activeFiltersCount,
    filters,
    previousSearch,
  ]);

  // Preparar opciones para dropdowns con valores por defecto seguros
  const categories = useMemo(
    () => ["Todas", ...(filterOptions?.categories || [])],
    [filterOptions?.categories]
  );

  const brands = useMemo(
    () => ["Todas", ...(filterOptions?.brands || [])], // ✅ NUEVO: Array de marcas
    [filterOptions?.brands]
  );

  const models = useMemo(
    () => ["Todas", ...(filterOptions?.models || [])], // ✅ ACTUALIZADO: Array de modelos
    [filterOptions?.models]
  );

  const ubicaciones = useMemo(
    () => ["Todas", ...(filterOptions?.locations || [])],
    [filterOptions?.locations]
  );

  // ✅ CORREGIDO: Estados válidos del backend (operativo, no operativo, null)
  const estados = ["Todas", "operativo", "no operativo"];

  const allFeatures = filterOptions.features;

  // Generar rangos de precio dinámicos
  const priceRanges = useMemo(() => {
    if (!filterOptions.priceRange.max) return [];

    const { min, max } = filterOptions.priceRange;
    const range = max - min;
    const step = Math.ceil(range / 4);

    return [
      {
        label: `Hasta $${(min + step / 1000000).toFixed(0)}M`,
        min: min,
        max: min + step,
      },
      {
        label: `$${(min + step / 1000000).toFixed(0)}M - $${(
          (min + step * 2) /
          1000000
        ).toFixed(0)}M`,
        min: min + step,
        max: min + step * 2,
      },
      {
        label: `$${((min + step * 2) / 1000000).toFixed(0)}M - $${(
          (min + step * 3) /
          1000000
        ).toFixed(0)}M`,
        min: min + step * 2,
        max: min + step * 3,
      },
      {
        label: `Más de $${((min + step * 3) / 1000000).toFixed(0)}M`,
        min: min + step * 3,
        max: max,
      },
    ];
  }, [filterOptions.priceRange]);

  // Actualizar filtros con validación
  const updateFilters = useCallback(
    (newFilters) => {
      console.log("🔄 Updating server filters:", newFilters);

      setFilters((prev) => {
        const updated = { ...prev, ...newFilters };
        console.log("📊 Server filters updated:", updated);

        // Sincronizar con URL cuando los filtros se actualizan directamente
        const queryParams = filtersToQueryParams(updated);
        if (queryParams) {
          navigate(`?${queryParams}`, { replace: true });
        } else {
          // Si no hay filtros activos, limpiar la URL
          navigate("?", { replace: true });
        }

        return updated;
      });
    },
    [navigate]
  );

  // Limpiar todos los filtros
  const clearAllFilters = useCallback(() => {
    const clearedFilters = {
      search: "",
      category: "Todas",
      brand: "Todas", // ✅ NUEVO: Filtro de marca (VOLVO, FORD, etc.)
      model: "Todas", // ✅ ACTUALIZADO: Filtro de modelo (FH, RANGER, etc.)
      priceRange: [
        filterOptions.priceRange.min || 0,
        filterOptions.priceRange.max || 500000000,
      ],
      yearRange: [
        filterOptions.yearRange.min || 2015,
        // 🔧 FIX: Usar el año máximo real del backend, no 2025
        filterOptions.yearRange.max || 2022,
      ],
      estado: "Todas", // ✅ CORREGIDO: usar estado
      ubicacion: "Todas",
      features: [],
    };

    console.log("🧹 Clearing all server filters to:", clearedFilters);

    // Actualizar filtros reales inmediatamente
    updateFilters(clearedFilters);

    // También limpiar filtros temporales
    // setTempFilters(clearedFilters);
    setHasUnappliedChanges(false);

    // 🔥 DISPARAR EVENTO PARA FORZAR RECARGA DE PRODUCTOS SIN FILTROS
    window.dispatchEvent(
      new CustomEvent("filtersApplied", {
        detail: {
          filters: clearedFilters,
          timestamp: Date.now(),
          action: "clear",
        },
      })
    );

    // Sincronizar URL
    navigate(`?${filtersToQueryParams(clearedFilters)}`, { replace: true });
  }, [updateFilters, filterOptions, navigate]);

  // Generar parámetros de API desde filtros
  const getApiParams = useCallback(
    (additionalParams = {}) => {
      const apiParams = {
        ...additionalParams,
        area: 1, // ✅ NUEVO: Filtrar por área "Bienes" por defecto
      };

      // Solo agregar filtros activos a los parámetros
      if (filters.search && filters.search.trim()) {
        apiParams.search = filters.search.trim();
      }
      if (filters.category && filters.category !== "Todas") {
        // 🔧 PRUEBA: Enviar en minúsculas para testear case sensitivity
        apiParams.category = filters.category.toLowerCase();
        console.log(
          "🔍 CATEGORY FILTER APPLIED:",
          filters.category,
          "→ SENT AS:",
          apiParams.category
        );
      }
      if (filters.brand && filters.brand !== "Todas") {
        apiParams.brand = filters.brand; // Enviamos como "brand" a la API
        console.log("🔍 BRAND FILTER APPLIED:", filters.brand);
      }
      if (filters.model && filters.model !== "Todas") {
        apiParams.model = filters.model; // Enviamos como "model" a la API
        console.log("🔍 MODEL FILTER APPLIED:", filters.model);
      }
      if (filters.estado && filters.estado !== "Todas") {
        // ✅ CORREGIDO: usar estado
        apiParams.estado = filters.estado; // Valores: "operativo", "no operativo"
        console.log("🔍 ESTADO FILTER APPLIED:", filters.estado);
      }
      if (filters.ubicacion && filters.ubicacion !== "Todas") {
        apiParams.location = filters.ubicacion;
      }

      // Rangos solo si no son los valores por defecto
      const isCustomPriceRange =
        filters.priceRange[0] !== filterOptions.priceRange.min ||
        filters.priceRange[1] !== filterOptions.priceRange.max;

      if (isCustomPriceRange) {
        apiParams.minPrice = filters.priceRange[0];
        apiParams.maxPrice = filters.priceRange[1];
      }

      const isCustomYearRange =
        filters.yearRange[0] !== filterOptions.yearRange.min ||
        filters.yearRange[1] !== filterOptions.yearRange.max;

      if (isCustomYearRange) {
        apiParams.minYear = filters.yearRange[0];
        apiParams.maxYear = filters.yearRange[1];
      }

      // Features como array
      if (filters.features && filters.features.length > 0) {
        apiParams.features = filters.features;
      }

      console.log("🔍 FINAL API PARAMS:", apiParams);
      return apiParams;
    },
    [filters, filterOptions]
  );

  // Formatear precio
  const formatPrice = useCallback((value) => {
    return `$${(value / 1000000).toFixed(0)}M`;
  }, []);

  // Función para reintentar carga
  const retry = useCallback(() => {
    console.log("🔄 Retrying filter options load...");
    loadFilterOptions();
  }, [loadFilterOptions]);

  // Contar filtros activos
  useEffect(() => {
    const activeCount = Object.entries(filters).filter(([key, value]) => {
      if (key === "priceRange") {
        return (
          value[0] !== filterOptions.priceRange.min ||
          value[1] !== filterOptions.priceRange.max
        );
      }
      if (key === "yearRange") {
        return (
          value[0] !== filterOptions.yearRange.min ||
          value[1] !== filterOptions.yearRange.max
        );
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (typeof value === "string") {
        return value !== "" && value !== "Todas";
      }
      return false;
    }).length;

    setActiveFiltersCount(activeCount);
  }, [filters, filterOptions]);

  // Detectar cambios en filtros para debugging
  useEffect(() => {
    console.log("🎯 Server filters changed:", {
      filters,
      activeCount: activeFiltersCount,
      apiParams: getApiParams(),
    });
    console.log(
      "🔍 useServerFilters - Current filters object:",
      JSON.stringify(filters, null, 2)
    );
  }, [filters, activeFiltersCount, getApiParams]);

  return {
    // Estado
    filters: stableFilters, // 🔧 EXPORTAR stableFilters en lugar de filters originales
    tempFilters,
    hasUnappliedChanges,
    loading: optionsLoading,
    error: optionsError,
    activeFiltersCount,

    // Opciones para filtros
    categories,
    brands, // ✅ NUEVO: Array de marcas
    models, // ✅ ACTUALIZADO: Array de modelos
    ubicaciones,
    estados, // ✅ CORREGIDO: exportar estados en lugar de conditions
    allFeatures,
    priceRanges,

    // Funciones
    updateFilters,
    updateTempFilters,
    applyFilters,
    resetTempFilters,
    clearAllFilters,
    formatPrice,
    retry,
    getApiParams,

    // Metadatos
    filterOptions, // Exportar filterOptions para uso en componentes
    rawFilters: filters, // 🔧 NUEVO: Exportar filtros originales para debugging si se necesitan
  };
};

export default useServerFilters;
